import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FaChevronLeft, FaChevronRight, FaTrash } from 'react-icons/fa';
import ReimaginedArtworkDetailsModal, {
  ReimaginedArtworkExpandedView,
} from './ReimaginedArtworkDetailsModal';
import { auth } from '../firebase';
// No longer need Firestore imports since we're not fetching data
import { API_ENDPOINTS } from '../config/backendConfig';
import { formatActivityDate } from '../utils/date';

const ReimagineFeedbackScrollerCard = ({
  originalArtworkUrl,
  originalArtworkTitle,
  onDeleteActivity,
  reimagineActivities = [] // Receive activities as prop
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [user, setUser] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [expandedFeedback, setExpandedFeedback] = useState({});

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUser(user);
    });
    return () => unsubscribe();
  }, []);

  const reimagineActivitiesKey = useMemo(
    () => (reimagineActivities || []).map((a) => a?.id || '').filter(Boolean).join('|'),
    [reimagineActivities]
  );
  const prevReimagineActivitiesKeyRef = useRef(reimagineActivitiesKey);

  useEffect(() => {
    if (prevReimagineActivitiesKeyRef.current === reimagineActivitiesKey) return;
    prevReimagineActivitiesKeyRef.current = reimagineActivitiesKey;
    const len = reimagineActivities?.length || 0;
    setCurrentIndex((prev) => (len === 0 ? 0 : Math.min(prev, len - 1)));
  }, [reimagineActivitiesKey, reimagineActivities]);

  // Reset expanded feedback when current activity changes
  useEffect(() => {
    setExpandedFeedback({});
  }, [currentIndex]);

  const currentActivity = reimagineActivities[currentIndex];

  // Activities are now passed as props, no need to fetch

  const handlePrevious = () => {
    setCurrentIndex(prev => 
      prev === 0 ? reimagineActivities.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex(prev => 
      prev === reimagineActivities.length - 1 ? 0 : prev + 1
    );
  };

  const toggleFeedbackExpansion = (feedbackIndex) => {
    setExpandedFeedback(prev => ({
      ...prev,
      [feedbackIndex]: !prev[feedbackIndex]
    }));
  };

  const resolveImageUrl = (imageUrl) => {
    if (!imageUrl || typeof imageUrl !== 'string') return '';
    
    // Check if it's a BFL URL that needs proxying
    if (imageUrl.includes('bfl.ai') || imageUrl.includes('delivery-eu4.bfl.ai')) {
      return `${API_ENDPOINTS.backendBase}/proxy-image?url=${encodeURIComponent(imageUrl)}`;
    }
    
    return imageUrl;
  };

  const getExpandedImage = (activity) => {
    console.log('🔍 getExpandedImage called with activity:', activity);
    
    // Check all possible image URL fields
    const possibleUrls = [
      activity?.transformedImageUrl,
      activity?.imageUrl,
      activity?.result?.imageUrl,
      activity?.metadata?.transformedImageUrl,
      activity?.metadata?.imageUrl,
      activity?.data?.imageUrl,
      activity?.transformedImage,
      activity?.resultImageUrl
    ];
    
    const validUrl = possibleUrls.find(url => url && typeof url === 'string' && url.length > 0);
    
    console.log('🔍 Possible URLs:', possibleUrls);
    console.log('🔍 Selected URL:', validUrl);
    
    // Resolve the URL to handle BFL URLs properly
    return resolveImageUrl(validUrl || '');
  };

  // No loading state needed since activities are passed as props

  console.log('🎨 ReimagineFeedbackScrollerCard render:', {
    reimagineActivitiesCount: reimagineActivities.length,
    originalArtworkUrl,
    user: !!user,
    currentActivity: currentActivity ? {
      id: currentActivity.id,
      title: currentActivity.title,
      feature: currentActivity.metadata?.feature
    } : null
  });
  
  if (!reimagineActivities || reimagineActivities.length === 0) {
    console.log('🎨 No reimagine activities provided, not rendering card');
    return null;
  }

  return (
    <>
      {/* Main Card */}
      <div 
        className="bg-gradient-to-br from-orange-50 to-yellow-50 backdrop-blur-sm border-2 border-orange-200 rounded-2xl p-3 shadow-xl relative overflow-hidden cursor-pointer hover:shadow-2xl hover:scale-105 transition-all duration-300"
        onClick={() => {
          try { window.dispatchEvent(new Event('close-global-details-modal')); } catch (err) {}
          setShowDetailsModal(true);
        }}
      >
        {/* Fun background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-2 right-2 text-4xl rotate-12">🎨</div>
          <div className="absolute bottom-2 left-2 text-3xl -rotate-12">✨</div>
          <div className="absolute top-1/2 left-1/2 text-2xl transform -translate-x-1/2 -translate-y-1/2 rotate-45">🌈</div>
        </div>

        {/* Strip Label */}
        <div className="absolute -top-1 left-4 bg-gradient-to-r from-orange-400 to-pink-400 text-white px-2 py-0.5 rounded-b text-xs font-bold shadow-md z-20">
          Re-imagined Artwork
        </div>

              {/* Transformed Artwork Only */}
      <div className="relative mb-2 z-10 mt-3">
        <div className="relative h-28 w-full rounded-2xl overflow-hidden border-2 border-orange-300 bg-gradient-to-br from-yellow-50 to-orange-50 shadow-lg">
            <img 
              src={getExpandedImage(currentActivity)} 
              alt={`${currentActivity?.metadata?.prompt || 'Transformed'} artwork`} 
              className="w-full h-full object-cover"
              onLoad={() => {
                console.log('✅ Image loaded successfully:', getExpandedImage(currentActivity));
              }}
              onError={(e) => {
                console.error('❌ Failed to load image:', getExpandedImage(currentActivity));
                e.target.style.display = 'none';
                const fallback = e.target.parentElement.querySelector('.image-fallback');
                if (fallback) fallback.style.display = 'flex';
              }}
            />
            {/* Fallback when image fails to load */}
            <div className="image-fallback hidden absolute inset-0 items-center justify-center bg-gradient-to-br from-yellow-50 to-orange-50">
              <div className="text-center">
                <div className="text-4xl mb-2">🎨</div>
                <p className="text-sm text-orange-600 font-medium">
                  Re-imagined Artwork
                </p>
              </div>
            </div>
            

            
            {/* Delete button on bottom right of image thumbnail */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                // Call parent's delete handler directly - it will show confirmation modal
                if (onDeleteActivity && currentActivity) {
                  onDeleteActivity(currentActivity.id);
                }
              }}
              className="absolute bottom-1 right-1 text-red-400 hover:text-red-600 p-1 rounded-full hover:bg-red-100 transition-all duration-200 opacity-70 hover:opacity-100 z-30"
              title="Delete this creation"
            >
              <FaTrash size={8} />
            </button>
          </div>
        </div>

        <div className="flex justify-between items-center mb-1 relative z-10">
          <div className="text-center flex-1">
            <p className="text-xs text-orange-700 font-medium">
              {formatActivityDate(currentActivity)}
            </p>
          </div>
        </div>

        {/* View Details Button */}
        <div className="relative z-10 mt-1">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              try { window.dispatchEvent(new Event('close-global-details-modal')); } catch (err) {}
              setShowDetailsModal(true);
            }}
            className="w-full bg-gradient-to-r from-orange-500 to-amber-400 hover:from-orange-600 hover:to-amber-500 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105"
            style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}
          >
            View details
          </button>
        </div>
      </div>

      <ReimaginedArtworkDetailsModal
        open={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        originalArtworkUrl={originalArtworkUrl}
        transformedImageUrl={getExpandedImage(currentActivity)}
        activity={currentActivity}
        expandedFeedback={expandedFeedback}
        onToggleFeedback={toggleFeedbackExpansion}
        onExpandImage={() => {
          setShowDetailsModal(false);
          setExpanded(true);
        }}
        showNavigation={reimagineActivities.length > 1}
        currentIndex={currentIndex}
        totalCount={reimagineActivities.length}
        onPrevious={handlePrevious}
        onNext={handleNext}
      />

      <ReimaginedArtworkExpandedView
        open={expanded}
        imageUrl={getExpandedImage(currentActivity)}
        onClose={() => {
          setExpanded(false);
          setShowDetailsModal(true);
        }}
      />
    </>
  );
};

export default ReimagineFeedbackScrollerCard;
